// Compliance Requirements (Brevity parity): define the qualifications each role MUST
// hold to be compliant. Workers missing/expired against their role's list are flagged
// non-compliant on their profile, the staff list, the compliance report, and the Scheduler.
import React, { useState, useEffect, useCallback } from 'react';
import { ShieldCheck, Plus, X, Loader2 } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const ROLES = [
  { key: 'carer',          label: 'Support Worker' },
  { key: 'support_worker', label: 'Support Worker (alt)' },
  { key: 'office_user',    label: 'Office / Coordinator' },
];

export default function ComplianceRequirements() {
  const [reqs, setReqs] = useState([]);
  const [qualNames, setQualNames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({}); // role -> in-progress qualification name
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/staff/compliance/requirements');
      setReqs(Array.isArray(data) ? data : []);
    } catch { /* table may be new */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    api.get('/smart-match')
      .then(r => setQualNames([...new Set((r.data?.qualifications || []).map(q => q.name).filter(Boolean))].sort()))
      .catch(() => {});
  }, [load]);

  const add = async (role) => {
    const name = (drafts[role] || '').trim();
    if (!name) return;
    setBusy(true);
    try {
      await api.post('/staff/compliance/requirements', { role, qualificationName: name });
      setDrafts(d => ({ ...d, [role]: '' }));
      await load();
    } catch { toast.error('Failed to add'); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/staff/compliance/requirements/${id}`); await load(); }
    catch { toast.error('Failed to remove'); }
  };

  if (loading) return <div className="flex items-center gap-2 text-slate-400 text-sm py-4"><Loader2 size={14} className="animate-spin" /> Loading…</div>;

  return (
    <div className="space-y-5">
      <p className="text-sm text-slate-500 -mt-1">
        List the qualifications each role must hold. A worker missing one — or whose certificate has expired —
        is flagged <span className="font-semibold text-rose-600">non-compliant</span> on their profile, the staff list,
        and when you try to roster them.
      </p>

      <datalist id="qual-name-options">
        {qualNames.map(n => <option key={n} value={n} />)}
      </datalist>

      {ROLES.map(({ key, label }) => {
        const items = reqs.filter(r => r.role === key);
        return (
          <div key={key} className="border border-slate-200 rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-100">
              <ShieldCheck size={14} className="text-emerald-600" />
              <h4 className="text-sm font-semibold text-slate-800">{label}</h4>
              <span className="ml-auto text-[11px] text-slate-400">{items.length} required</span>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex flex-wrap gap-2">
                {items.length === 0
                  ? <span className="text-xs text-slate-400">No required qualifications for this role yet.</span>
                  : items.map(it => (
                    <span key={it.id} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700">
                      {it.qualification_name}
                      <button onClick={() => remove(it.id)} className="hover:opacity-70"><X size={11} /></button>
                    </span>
                  ))}
              </div>
              <div className="flex gap-2">
                <input
                  list="qual-name-options"
                  className="input-field text-sm flex-1"
                  placeholder="e.g. First Aid Certificate, NDIS Worker Screening…"
                  value={drafts[key] || ''}
                  onChange={e => setDrafts(d => ({ ...d, [key]: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(key); } }}
                />
                <button onClick={() => add(key)} disabled={busy || !(drafts[key] || '').trim()}
                  className="flex items-center gap-1.5 px-3 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-xs font-semibold disabled:opacity-50">
                  <Plus size={13} /> Add
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
